/**
 * Quotes & Costing in the role model (docs/quotes.md §Roles): the six
 * permission areas, what each built-in role holds in them and the Account
 * Manager role. shared/areas.ts spreads QUOTE_AREAS into AREAS and calls
 * applyQuoteGrants() after its BUILTIN_ROLES literal, so no role entry there
 * changes. A member holds one role: an Account Manager who should see
 * margin gets a custom role, duplicated from Account Manager, with Quote
 * cost and margin read.
 */
import type { Area, BuiltinRole, Grant, Grants } from "./areas";

export const QUOTE_AREAS: Area[] = [
  {
    key: "quotes", section: "Business", label: "Quotes", icon: "receipt",
    description: "Quotes for customers, their approvals and outcomes.",
    levels: ["none", "read", "custom", "write"],
    actions: [
      { key: "create", label: "Create quotes and edit your own" },
      { key: "edit_any", label: "Edit anyone's quotes" },
      { key: "send", label: "Send quotes and record answers" },
      { key: "export", label: "Export PDF and Excel" },
      { key: "approve", label: "Approve quotes within the discount cap" },
      { key: "approve_over_cap", label: "Approve discounts above the cap" },
    ],
  },
  {
    key: "quote_margin", section: "Business", label: "Quote cost and margin", icon: "money",
    description: "Cost and gross margin on quotes and the internal deal sheet.",
    levels: ["none", "read"],
    actions: [],
  },
  {
    key: "quote_skus", section: "Business", label: "SKUs and prices", icon: "tag",
    description: "The SKU catalogue and its dated prices.",
    levels: ["none", "read", "custom", "write"],
    actions: [
      { key: "edit", label: "Create, edit and retire SKUs" },
      { key: "prices", label: "Change prices" },
    ],
  },
  {
    key: "quote_costing", section: "Business", label: "Cost profiles", icon: "calculator",
    description: "Unit costs and sizing that quote margins are computed from.",
    levels: ["none", "read", "custom", "write"],
    actions: [
      { key: "edit", label: "Change cost items and sizing" },
      { key: "publish", label: "Publish and retire profiles" },
    ],
  },
  {
    key: "quote_settings", section: "Business", label: "Quote settings", icon: "settings",
    description: "Company details, numbering, tax, exchange rates, rules and documents.",
    levels: ["none", "read", "custom", "write"],
    actions: [
      { key: "company", label: "Company, numbering and validity" },
      { key: "tax_fx", label: "Tax and exchange rates" },
      { key: "rules", label: "Pricing rules, approval rules and margin floor" },
      { key: "documents", label: "Presets, payment terms and terms and conditions" },
    ],
  },
  {
    key: "quote_bank", section: "Business", label: "Bank details", icon: "building-bank",
    description: "The bank accounts printed on approved quotes.",
    levels: ["none", "read", "write"],
    actions: [],
  },
];

export const QUOTE_AREA_KEYS = QUOTE_AREAS.map((a) => a.key);

/**
 * The safeguard on activating a bank account (shared/areas.ts
 * SECOND_APPROVER_ACTIONS): the last editor can't activate it, a second
 * member does. On for every built-in role that writes bank details except
 * the Owner, who activates their own edit (routes/quotes/bank.ts).
 */
export const BANK_ACTIVATE = "quotes.bank_activate";

export const ACCOUNT_MANAGER_ROLE = "builtin:account_manager";
export const FINANCE_ROLE = "builtin:finance";

const W: Grant = { level: "write" };
const R: Grant = { level: "read" };

/** What each built-in role holds in the six areas. A role not listed here gets none in all six. The Owner keeps its own definition (all write). */
export const QUOTE_GRANTS: Record<string, Grants> = {
  "builtin:super_admin": { quotes: W, quote_margin: R, quote_skus: W, quote_costing: W, quote_settings: W, quote_bank: W },
  [FINANCE_ROLE]: { quotes: W, quote_margin: R, quote_skus: W, quote_costing: W, quote_settings: W, quote_bank: W },
  [ACCOUNT_MANAGER_ROLE]: { quotes: { level: "custom", actions: ["create", "send", "export"] }, quote_skus: R, quote_settings: R },
  "builtin:security_auditor": { quotes: R, quote_margin: R, quote_skus: R, quote_costing: R, quote_settings: R },
};

/** Roles whose grants this module leaves as defined (the Owner holds everything). */
const LEFT_AS_DEFINED = new Set(["builtin:owner"]);

export const ACCOUNT_MANAGER: BuiltinRole = {
  id: ACCOUNT_MANAGER_ROLE,
  name: "Account Manager",
  description: "Builds and sends quotes for their customers.",
  grants: {},
  safeguards: { session_hours: 10 },
};

/**
 * Sets the six quote areas on every built-in role from QUOTE_GRANTS,
 * overriding what all("read") or all("write") gave; an area a role is not
 * granted becomes none, and a role that writes bank details carries the
 * BANK_ACTIVATE safeguard. Adds the Account Manager role when missing. Runs
 * once at module load (shared/areas.ts); idempotent.
 */
export function applyQuoteGrants(roles: BuiltinRole[]): void {
  if (!roles.some((r) => r.id === ACCOUNT_MANAGER_ROLE)) roles.push(ACCOUNT_MANAGER);
  for (const r of roles) {
    if (LEFT_AS_DEFINED.has(r.id)) continue;
    const mine = QUOTE_GRANTS[r.id] ?? {};
    for (const key of QUOTE_AREA_KEYS) r.grants[key] = mine[key] ? { ...mine[key], actions: mine[key].actions ? [...mine[key].actions] : undefined } : { level: "none" };
    if (r.grants.quote_bank?.level === "write" && !(r.safeguards.second_approver ?? []).includes(BANK_ACTIVATE)) r.safeguards.second_approver = [...(r.safeguards.second_approver ?? []), BANK_ACTIVATE];
    if (r.id === FINANCE_ROLE) r.description = "Revenue, pricing and discounts; approves quotes and discounts above the cap; costing, bank details, tax and exchange rates.";
  }
}
